import { useEffect, useState } from "react";
import { buildFeedback } from "@contentgen/shared";
import { RoutePicker } from "./RoutePicker.js";
import { api } from "../lib/api.js";
import type { ContentType, ModelRoute, OutputWithUrl } from "../lib/types.js";

// The three ways to regenerate a deliverable (D-10 / D-11):
//   1. same route again, with the rejection feedback injected
//   2. the next recommended route (current one excluded)
//   3. a route the reviewer picks by hand
// All three send the feedback text shown in the textarea, editable first, as
// `feedbackText` on POST /api/deliverables/:id/generate.

const btnGhost =
  "rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 " +
  "disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

const btnPrimary =
  "rounded-md bg-(--accent) px-3 py-1.5 text-xs font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

export function RegeneratePanel({
  contentType,
  currentRouteId,
  outputs,
  busy,
  onRegenerate,
}: {
  contentType: ContentType;
  currentRouteId?: string;
  outputs: OutputWithUrl[];
  busy?: boolean;
  onRegenerate: (opts: { routeId?: string; feedbackText: string }) => void;
}) {
  const [feedback, setFeedback] = useState("");
  const [manualRouteId, setManualRouteId] = useState("");
  const [alternative, setAlternative] = useState<ModelRoute | null>(null);
  const [altError, setAltError] = useState<string | null>(null);

  // Reset the editable feedback whenever the underlying ratings change.
  useEffect(() => {
    setFeedback(buildFeedback(outputs));
  }, [outputs]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ contentType });
    if (currentRouteId) params.set("exclude", currentRouteId);
    api<{ items: ModelRoute[] }>(`/api/recommend?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setAlternative(res.items[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setAltError("Could not load a recommended alternative.");
      });
    return () => {
      cancelled = true;
    };
  }, [contentType, currentRouteId]);

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-900/40">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Regenerate</div>

      <label className="mt-2 block text-xs text-gray-500 dark:text-gray-400">
        Feedback injected into the next attempt
      </label>
      <textarea
        rows={3}
        className={`${inputCls} mt-1`}
        placeholder="No rejection comments yet - add a thumbs-down comment to feed one in."
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={busy}
          onClick={() => onRegenerate({ routeId: currentRouteId, feedbackText: feedback })}
        >
          Retry same route with feedback
        </button>

        <button
          type="button"
          className={btnGhost}
          disabled={busy || !alternative}
          onClick={() => onRegenerate({ routeId: alternative?.routeId, feedbackText: feedback })}
        >
          {alternative ? `Try ${alternative.displayName}` : "No alternative"}
        </button>
      </div>
      {altError && <p className="mt-1 text-xs text-amber-600">{altError}</p>}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-56 flex-1">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Or pick a route</label>
          <RoutePicker contentType={contentType} value={manualRouteId} onChange={setManualRouteId} />
        </div>
        <button
          type="button"
          className={btnGhost}
          disabled={busy}
          onClick={() => onRegenerate({ routeId: manualRouteId || undefined, feedbackText: feedback })}
        >
          Regenerate with this route
        </button>
      </div>
    </div>
  );
}
